/**
 * Auto-synthesize editor text rows into real prompts before submitting a
 * TTS Flow. The wire format only allows `prompt` and `variable` rows — when
 * the user authors a `text` row inline in the two-pane editor, we hit the
 * Voice Library synthesize endpoint at save time to produce a prompt with
 * its own promptId, then replace the row with `{ttsType: 'prompt', value:
 * <newPromptId>}`. Wire output remains byte-identical to legacy.
 */
import { api } from '@/lib/api/client';
import type {
  TtsConvertRequest,
  TtsConvertResponse,
  TtsFinalizeRequest,
} from '@/features/voice-library/types';
import type { TtsFlowFormValues, TtsRow } from './types';

interface SynthesizeInput {
  userId: number | string;
  values: TtsFlowFormValues;
}

/**
 * Walk `values.ttsRows` and synthesize any inline-text rows. Returns the
 * row array with text rows replaced by prompt rows. Throws if any
 * convert/finalize call fails — the caller decides how to surface the
 * error.
 */
export async function synthesizePendingTextRows({
  userId,
  values,
}: SynthesizeInput): Promise<TtsRow[]> {
  if (!values.language || !values.gender) {
    throw new Error('Pick a language and a voice before saving.');
  }

  const out: TtsRow[] = [];

  for (let i = 0; i < values.ttsRows.length; i++) {
    const row = values.ttsRows[i]!;

    // Anything that isn't a brand-new text row passes through unchanged.
    const isTextRow =
      typeof row.text === 'string' && row.text.length > 0 && row.ttsType !== 'variable';

    if (!isTextRow) {
      // Strip the editor-only fields so the wire format is clean.
      const { text: _text, previewUrl: _previewUrl, previewTtsId: _previewTtsId, ...rest } = row;
      void _text;
      void _previewUrl;
      void _previewTtsId;
      out.push(rest as TtsRow);
      continue;
    }

    // Resolve a category for the generated prompt. We always tag these as
    // "tts" so they're easy to identify in the library + don't pollute the
    // pickable categories.
    const filename = `ttsflow_${(values.ttsFlowName || 'flow').replace(/[^a-zA-Z0-9]+/g, '_').slice(0, 40)}_${Date.now()}_${i + 1}`;

    // Reuse a previously-previewed render if available — saves a credit.
    let ttsId = row.previewTtsId;
    let downloadUrl = row.previewUrl ?? '';
    let promptDuration = 0;
    let characterCount = row.text!.length;

    if (!ttsId) {
      const convertReq: TtsConvertRequest = {
        userId,
        gender: values.gender as TtsConvertRequest['gender'],
        textScript: row.text!,
        characterCount: row.text!.length,
        language: values.language,
        filename,
        category: 'tts',
      };
      const res = await api.post<TtsConvertResponse>(
        '/api/obd/convertion/texttospeech',
        convertReq,
      );
      if (res.status === 'fail') {
        throw new Error(res.msg || 'Synthesis failed for an inline text row.');
      }
      ttsId = res.ttsId;
      downloadUrl = res.downloadUrl;
      promptDuration = res.promptDuration;
      characterCount = res.characterCount;
    }

    // Promote the pending TTS row into an active library prompt.
    const finalizeReq: TtsFinalizeRequest = {
      id: ttsId!,
      userId,
      status: 1,
      dowmloadUrl: downloadUrl,
      promptName: filename,
      promptDuration: String(promptDuration),
      characterCount,
      category: 'tts',
    };
    const finalizeRes = await api
      .post<{ message?: string; promptId?: number }>('/api/obd/change/tts/status', finalizeReq)
      .catch((e) => {
        throw e instanceof Error ? e : new Error(String(e));
      });

    if (finalizeRes?.message == 'No TTS Found ') {
      throw new Error('No TTS Found');
    }
    // The legacy backend's `change/tts/status` doesn't reliably return a
    // promptId, but it does match prompts by ttsId on lookup. We store the
    // ttsId as the row's value — the dialplan resolves it the same way the
    // legacy app's saved flows do.
    const newPromptId = finalizeRes?.promptId ?? ttsId!;

    out.push({
      ttsType: 'prompt',
      value: String(newPromptId),
      variableType: '',
    });
  }

  return out;
}
